"use strict";
'use es6';

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.makePartial = makePartial;
exports.makeUniqueId = makeUniqueId;
exports.bindHandlers = bindHandlers;
exports.bindAll = bindAll;

var _invariant = _interopRequireDefault(require("react-utils/invariant"));

var _memoize = _interopRequireDefault(require("transmute/memoize"));

var _partial = _interopRequireDefault(require("transmute/partial"));

var _react = require("react");

var _uniqueId = _interopRequireDefault(require("transmute/uniqueId"));

/**
 * Returns a function (similar to `_.partial`) that memoizes generated functions.
 * This can be a useful performance techinque in conjunction with components
 * that use `PureRenderMixin` or `PureComponent`.
 *
 * It's an alternative to ComponentWithPartials when JS class components which
 * don't support mixins.
 *
 * @example
 * class MyComponent extends Component {
 *   constructor() {
 *     super();
 *     bindHandlers(this);
 *     this.partial = makePartial();
 *     this.state = {firstname: '', lastname: ''};
 *   }
 *
 *   handleChange(fieldName, {target: {value}}) {
 *     this.setState({[fieldName]: value});
 *   }
 *
 *   render() {
 *     return (
 *       <div>
 *         <input
 *           name="firstname
 *           onChange={this.partial(this.handleChange, 'firstname')}
 *           value={this.state.firstname}
 *         />
 *         <input
 *           name="lastname
 *           onChange={this.partial(this.handleChange, 'lastname')}
 *           value={this.state.lastname}
 *         />
 *       </div>
 *     );
 *   }
 * }
 *
 * @return {(fn: Function, ...args: Array<any>) => Function}
 */
function makePartial() {
  return (0, _memoize.default)(_partial.default);
}
/**
 * Returns a function that returns a consistent, but unique id for `prefix`.
 *
 * @example
 * class MyComponent extends Component {
 *   constructor() {
 *     super();
 *     this.uniqueId = makeUniqueId();
 *   }
 *
 *   render() {
 *     return (
 *       <div id={this.uniqueId('firstname')}>
 *        // ...
 *       </div>
 *     );
 *   }
 * }
 *
 * @return {(prefix: string) => string}
 */


function makeUniqueId() {
  return (0, _memoize.default)(_uniqueId.default);
}
/**
 * Utility for binding event handler methods to the component instance.
 *
 * It's similar to `_.bindAll`, but only binds the instance to methods prefixed
 * with "handle" (e.g. "handleClick").
 *
 * @example
 * class MyComponent extends Component {
 *   constructor() {
 *     super();
 *     bindHandlers(this);
 *   }
 *
 *   handleChange({target: {value}}) {
 *     this.setState({value});
 *   }
 *
 *   render() {
 *     return <div>...</div>;
 *   }
 * }
 *
 * @param {Component} component instance to be bound to the handlers
 */


var handlerPattern = /^handle\w+$/;

function bindHandlers(component) {
  (0, _invariant.default)(component instanceof _react.Component, 'expected `component` to be an React class component instance but got `%s`', component);
  Object.getOwnPropertyNames(component.constructor.prototype).forEach(function (key) {
    var method = component[key];

    if (typeof method === 'function' && handlerPattern.test(key)) {
      component[key] = method.bind(component);
    }
  });
}
/**
 * Utility which binds HubSpotty component methods to the component instance.
 * It binds all methods named with prefixes "handle" (like `bindHandlers` does)
 * as well as "get" and "render". It doesn't bind all other methods, unlike
 * `underscore.bindAll`. It's not advised to use this utility in new code.
 *
 * @example
 * class MyVerySpecialComponentConvertedFromCjsx extends Component {
 *   constructor() {
 *     super();
 *     bindAll(this);
 *   }
 *
 *   getData() {
 *     if (this.state.enabling && this.props.getSpecialData) {
 *       return this.props.getSpecialData()
 *     }
 *   }
 *
 *   renderHeader() {
 *     return <div data={this.getData()} />
 *   }
 *
 *   render() {
 *     return <div>
 *      {this.renderHeader()}
 *       …
 *     </div>;
 *   }
 * }
 *
 * @param {Component} component instance to be bound to the handlers
 */


var allPattern = /^(get|handle|render)\w+$/;

function bindAll(component) {
  (0, _invariant.default)(component instanceof _react.Component, 'expected `component` to be an React class component instance but got `%s`', component);
  Object.getOwnPropertyNames(component.constructor.prototype).forEach(function (key) {
    var method = component[key];

    if (typeof method === 'function' && allPattern.test(key)) {
      component[key] = method.bind(component);
    }
  });
}